import { FC, useCallback, useMemo, useEffect } from 'react'
// constants
import { MPC } from '@/components/shared/constants'
// types
import {
  SelectorProps,
  Asset,
} from '@/components/shared/components/selector/types'
import { ChainInfo } from '@/types'
// components
import { ChainSelector } from './Chain'
import { AssetSelector } from './Asset'

// Enhanced MPC Chain-Token Selector with automatic token filtering
export const Selector: FC<SelectorProps> = ({
  currentActiveChain,
  chainList,
  onChainChange,
  curSelectToken,
  onTokenChange,
  chainPoolBalances,
  walletType = MPC, // 默认为 MPC 钱包
  txType = 'deposit', // 默认为充值场景
}) => {
  // Helper: wallet allow filter (deposit/withdraw)
  type ChainWithAllow = ChainInfo & {
    allowAaDeposit?: boolean
    allowDeposit?: boolean
    allowAaWithdraw?: boolean
    allowWithdraw?: boolean
  }
  const walletAllows = useCallback(
    (c: ChainWithAllow) => {
      if (txType === 'withdraw') {
        return walletType === MPC
          ? c.allowAaWithdraw !== false
          : c.allowWithdraw !== false
      }
      // default deposit
      return walletType === MPC
        ? c.allowAaDeposit !== false
        : c.allowDeposit !== false
    },
    [txType, walletType]
  )

  // Build token list from all chains, keeping first-seen order and hiding tokens with no allowed chains
  const tokensDerived: Asset[] = useMemo(() => {
    const seen = new Set<string>()
    const order: Asset[] = []
    for (const chain of chainList as ChainWithAllow[]) {
      if (!walletAllows(chain)) continue
      for (const tk of chain.tokenList || []) {
        const symbol = tk.symbol || tk.token
        if (!symbol) continue
        // withdraw filter: only include tokens where withdrawEnable === true and pullOff === false
        const allowedOnThisChain =
          txType === 'withdraw'
            ? tk.withdrawEnable === true && tk.pullOff === false
            : true
        if (!allowedOnThisChain) continue
        if (!seen.has(symbol)) {
          seen.add(symbol)
          order.push({
            token: symbol,
            symbol,
            iconUrl: tk.iconUrl,
            decimals: tk.decimals,
            tokenAddress: tk.tokenAddress,
          })
        }
      }
    }
    return order
  }, [chainList, walletAllows, txType])

  // Note: selectedToken logic moved to currentTokenForSelector below

  // Handle chain change (keep current token)
  const handleChainChange = (chain: ChainInfo) => {
    onChainChange(chain.chainId)
  }

  // Handle token change: keep current chain if supported; otherwise switch to first supported chain
  const handleTokenChange = (token: Asset) => {
    const next = token.symbol || token.token
    onTokenChange(next)

    const supported: number[] = []
    for (const chain of chainList as ChainWithAllow[]) {
      if (!walletAllows(chain)) continue
      const has = (chain.tokenList || []).some(
        t => (t.symbol || t.token) === next
      )
      if (has) supported.push(chain.chainId)
    }
    if (
      !supported.includes(currentActiveChain.chainId) &&
      supported[0] !== undefined
    ) {
      onChainChange(supported[0])
    }
  }

  // Chains available for the currently selected token (token-first UX)
  const chainSelectorChains: ChainInfo[] = useMemo(() => {
    const selected =
      curSelectToken || tokensDerived[0]?.symbol || tokensDerived[0]?.token
    if (!selected) return []
    const result: ChainInfo[] = []
    for (const chain of chainList as ChainWithAllow[]) {
      if (!walletAllows(chain)) continue
      const tk = (chain.tokenList || []).find(t => {
        const matches = (t.symbol || t.token) === selected
        if (!matches) return false
        if (txType === 'withdraw') {
          return t.withdrawEnable === true && t.pullOff === false
        }
        return true
      })
      if (tk) {
        result.push({
          ...chain,
          tokenList: [
            {
              token: tk.token,
              symbol: tk.symbol,
              iconUrl: tk.iconUrl,
              decimals: tk.decimals,
              tokenAddress: tk.tokenAddress,
            },
          ],
        } as ChainInfo)
      }
    }
    return result
  }, [chainList, curSelectToken, tokensDerived, walletAllows, txType])

  const currentChainForSelector: ChainInfo = useMemo(() => {
    // 从 chainSelectorChains 中找到匹配的链，确保引用一致
    return (
      chainSelectorChains.find(
        chain => chain.chainId === currentActiveChain.chainId
      ) || chainSelectorChains[0]
    )
  }, [chainSelectorChains, currentActiveChain.chainId])

  // Tokens available (unique across allowed chains)
  const tokenSelectorTokens: Asset[] = useMemo(
    () => tokensDerived,
    [tokensDerived]
  )

  // Find the current selected token from tokenSelectorTokens to ensure reference consistency
  const currentTokenForSelector: Asset | undefined = useMemo(() => {
    // 如果没有可用的代币，返回 undefined
    if (tokenSelectorTokens.length === 0) {
      return undefined
    }

    // 从 tokenSelectorTokens 中找到匹配的 token，确保引用一致
    return (
      tokenSelectorTokens.find(
        token =>
          token.token === curSelectToken || token.symbol === curSelectToken
      ) || tokenSelectorTokens[0]
    )
  }, [tokenSelectorTokens, curSelectToken])

  // Defaults: pick first token and its first supported chain if none selected
  useEffect(() => {
    if (tokenSelectorTokens.length === 0) return
    const exists = tokenSelectorTokens.some(
      t => (t.symbol || t.token) === curSelectToken
    )
    if (!exists) {
      const first = tokenSelectorTokens[0]
      onTokenChange(first.symbol || first.token)
    }
  }, [tokenSelectorTokens, curSelectToken, onTokenChange])

  useEffect(() => {
    if (chainSelectorChains.length === 0) return
    const has = chainSelectorChains.some(
      c => c.chainId === currentActiveChain.chainId
    )
    if (!has) {
      onChainChange(chainSelectorChains[0].chainId)
    }
  }, [chainSelectorChains, currentActiveChain.chainId, onChainChange])

  return (
    <div className="space-y-4">
      {/* Asset Selection */}
      {tokenSelectorTokens.length > 0 && currentTokenForSelector && (
        <AssetSelector
          tokens={tokenSelectorTokens}
          selectedToken={currentTokenForSelector}
          onTokenChange={handleTokenChange}
        />
      )}
      {/* Account Selection */}

      {/* Chain Selection */}
      <ChainSelector
        chains={chainSelectorChains}
        selectedChain={currentChainForSelector}
        chainPoolBalances={chainPoolBalances}
        onChainChange={chain => {
          // Find the full chain data with tokens
          const fullChain = chainList.find(c => c.chainId === chain.chainId)
          if (fullChain) {
            handleChainChange(fullChain)
          }
        }}
        walletType={walletType}
        className="w-full"
      />
    </div>
  )
}
